/* Humbler collection + search: filter sheet (with the one-tap sort), infinite scroll (skeleton cards, URL kept on the
   page in view, place restored on back/refresh) and the preview-only filter/sort stand-in. */
(() => {
  const ui = () => window.HumblerUI;
  const isPreview = document.documentElement.dataset.humblerPreview === 'true';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const AUTO_PAGES = 3; // pages that load on their own per visit; after that "Load more" takes a tap (footer stays reachable)
  const AHEAD = '0px 0px 800px 0px'; // start fetching this far before the end of the grid
  const CARRY = /^(q|type|sort_by|filter\.|options\[)/; // query params every next/previous page keeps
  const STORE = 'hb-collection-place:';
  const lists = new Map(); // [data-hb-collection] section -> infinite-scroll state

  document.addEventListener('click', event => {
    const opener = event.target.closest('[data-hb-filters-open]');
    if (opener) { ui()?.openDialog(document.getElementById(opener.getAttribute('aria-controls')), opener); return; }

    const pager = event.target.closest('[data-hb-load-more], [data-hb-load-previous]');
    if (pager) {
      const state = lists.get(pager.closest('[data-hb-collection]'));
      if (!state || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
      event.preventDefault();
      // detail 0: activated from the keyboard (or a screen reader), not a pointer.
      load(state, pager.hasAttribute('data-hb-load-previous') ? 'previous' : 'next', { manual: true, keyboard: event.detail === 0 });
      return;
    }
    // Leaving for a product: point the URL at the page being looked at first, so back/refresh lands there.
    if (event.target.closest('[data-hb-collection-grid] a[href]')) syncUrl();
  });

  // Sort lives in the sheet only and applies at once when a radio is picked (tap or click). From the keyboard,
  // the arrow keys only move the choice, so a shopper can pass over options without reloading on each one;
  // Space or Enter applies it (as does the sheet's Apply button).
  const submitSort = form => { form.requestSubmit ? form.requestSubmit() : form.submit(); };
  let sortArrowed = false;
  document.addEventListener('keydown', event => {
    const radio = event.target.closest?.('[data-hb-sort-radio]');
    if (!radio || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key?.startsWith('Arrow')) {
      sortArrowed = true; // the change event this key fires is a move, not a pick
      setTimeout(() => { sortArrowed = false; }, 0);
    } else if (event.key === 'Enter' || (event.key === ' ' && radio.checked)) {
      // Space on an unchecked radio checks it natively, and that change applies it below.
      event.preventDefault();
      radio.checked = true;
      submitSort(radio.form);
    }
  });
  document.addEventListener('change', event => {
    const radio = event.target.closest('[data-hb-sort-radio]');
    if (radio && !sortArrowed) submitSort(radio.form);
  });
  // Closing the sheet without applying: a choice only moved with the arrow keys must not stay ticked (the sheet is
  // the only place the sort shows, and a later Apply would submit it).
  document.addEventListener('close', event => {
    if (!(event.target instanceof HTMLDialogElement)) return;
    event.target.querySelectorAll('[data-hb-sort-radio]').forEach(input => { input.checked = input.defaultChecked; });
  }, true);

  // Empty price fields are not sent, so the URL only carries filters the shopper actually set.
  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-hb-filter-form]');
    if (!form) return;
    // A new sort or filter reloads from page 1 (no page param in the form): stop anything still loading.
    lists.forEach(stop);
    form.querySelectorAll('input[type="number"]').forEach(input => { if (input.value === '') input.disabled = true; });
    setTimeout(() => form.querySelectorAll('input[type="number"]').forEach(input => { input.disabled = false; }), 0);
  });

  // Keep the current category pill in view when the row scrolls (e.g. "Rings" at the end of the list).
  const revealCurrentPill = () => document.querySelectorAll('.hb-collection__siblings').forEach(list => {
    const current = list.querySelector('[aria-current]');
    if (!current || list.scrollWidth <= list.clientWidth) return;
    const box = list.getBoundingClientRect();
    const pill = current.closest('li').getBoundingClientRect();
    if (pill.right > box.right) list.scrollLeft += pill.left - box.left - parseFloat(getComputedStyle(list).paddingLeft || 0);
  });

  /* ---------- infinite scroll ----------
     The server-rendered "Load more" link (paginate.next.url) stays the no-JS/SEO fallback and the trigger:
     an IntersectionObserver on its nav fetches the next page early, skeleton cards hold the exact cells the
     real cards take, and after AUTO_PAGES automatic pages the link waits for a tap. */

  // Absolute URL for a paginate link that keeps the shopper's sort, filters and search terms.
  // (Shopify's paginate URLs already carry them; the static local preview's do not.)
  function carry(href) {
    const url = new URL(href, location.href);
    const current = new URLSearchParams(location.search);
    for (const key of new Set(current.keys())) {
      if (CARRY.test(key) && !url.searchParams.has(key)) current.getAll(key).forEach(value => url.searchParams.append(key, value));
    }
    url.searchParams.delete('section_id');
    return url;
  }

  function setup(section) {
    if (lists.has(section)) return;
    const grid = section.querySelector('[data-hb-collection-grid]');
    const more = section.querySelector('[data-hb-more]');
    if (!grid || !more) return;
    const state = {
      section, grid, more,
      next: more.querySelector('[data-hb-load-more]'),
      previous: section.querySelector('[data-hb-load-previous]'),
      status: more.querySelector('[data-hb-shown]'),
      bar: more.querySelector('[data-hb-progress]'),
      error: more.querySelector('[data-hb-load-error]'),
      first: Number(more.dataset.hbFirst) || 1, // position of the first piece on screen (a deep link starts mid-list)
      count: Number(more.dataset.hbCount) || 0, // position of the last one
      total: Number(more.dataset.hbTotal) || 0,
      pageSize: Number(more.dataset.hbPageSize) || 24,
      auto: 0, busy: null, stopped: false, failed: false, grew: false, restored: false, held: false, observer: null
    };
    for (const link of [state.next, state.previous]) {
      if (!link) continue;
      link.href = carry(link.getAttribute('href')).href;
      link.dataset.label = link.textContent.trim();
    }
    lists.set(section, state);
    readTitle(section);
    watch(state);
  }

  function watch(state) {
    state.observer?.disconnect();
    state.observer = null;
    if (!state.next || state.stopped || state.failed || state.auto >= AUTO_PAGES || !('IntersectionObserver' in window)) return;
    // A fresh observer reports the current position at once, so a still-short grid keeps loading.
    state.observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) load(state, 'next');
    }, { rootMargin: AHEAD });
    state.observer.observe(state.more);
  }

  // Automatic loading waits while keyboard focus is on "Load more" (or anywhere past it, e.g. the footer): new
  // cards would land above that focus and push it out of sight. Enter on the link loads by hand instead, and
  // those cards land in view (see load()). Loading resumes once focus is back among the cards or let go.
  function focusHolds(state) {
    const active = document.activeElement;
    if (!active || active === document.body || active === document.documentElement) return false;
    return state.more.contains(active) || Boolean(state.more.compareDocumentPosition(active) & Node.DOCUMENT_POSITION_FOLLOWING);
  }
  function releaseHeld() {
    lists.forEach(state => {
      if (state.held && !focusHolds(state)) { state.held = false; watch(state); }
    });
  }
  document.addEventListener('focusin', releaseHeld);
  document.addEventListener('focusout', () => setTimeout(releaseHeld, 0)); // focus let go (to the page) fires no focusin

  // The part of the window the sticky header + toolbar leave visible once the toolbar is stuck. Uses the
  // toolbar's sticky offset, not its live position: it may be scrolled away right now and stick again on scroll.
  function band(state) {
    const bar = state.section.querySelector('.hb-collection__toolbar');
    const stuck = bar ? (parseFloat(getComputedStyle(bar).top) || 0) + bar.offsetHeight : 0;
    return { top: Math.max(0, stuck), bottom: window.innerHeight };
  }
  function inView(state, node) {
    const box = node.getBoundingClientRect();
    const { top, bottom } = band(state);
    return box.height > 0 && box.top >= top && box.bottom <= bottom;
  }
  // Scroll the least distance that shows the whole card (its top first if it is taller than the window).
  function bringIntoView(state, node) {
    const box = node.getBoundingClientRect();
    const { top, bottom } = band(state);
    let shift = 0;
    if (box.bottom > bottom) shift = Math.min(box.bottom - bottom + 16, box.top - top - 8);
    else if (box.top < top) shift = box.top - top - 8;
    if (Math.abs(shift) > 0.5) window.scrollBy({ top: shift, behavior: reduceMotion.matches ? 'instant' : 'smooth' });
  }

  function stop(state) {
    state.stopped = true;
    state.observer?.disconnect();
    state.observer = null;
    state.busy?.abort();
  }

  // Skeleton cards from the shared helper (assets/humbler-skeleton.js): built from the real card's own boxes,
  // so each is exactly the size of the card that replaces it. The local fallback (same boxes, styled in
  // humbler-collection.css) only matters if that script has not loaded.
  const fallbackCard = '<li><div class="hb-card hb-card--skeleton"><div class="hb-card__visual"><span class="hb-card__media hb-stage"></span></div>'
    + '<div class="hb-card__info"><span class="hb-card__title"></span><span class="hb-card__meta"></span><span class="hb-card__price"></span></div></div></li>';
  function skeletons(count) {
    const holder = document.createElement('template');
    holder.innerHTML = window.HumblerSkeleton?.cards?.(count) ?? fallbackCard.repeat(Math.max(0, count));
    return [...holder.content.children].map(node => {
      let item = node;
      if (node.tagName !== 'LI') { item = document.createElement('li'); item.append(node); }
      item.classList.add('hb-collection__item', 'hb-collection__item--skeleton');
      item.setAttribute('aria-hidden', 'true');
      return item;
    });
  }

  // Run a DOM change without moving what the shopper is looking at (also undoes browser scroll anchoring).
  function keep(reference, change) {
    const before = reference?.isConnected ? reference.getBoundingClientRect().top : null;
    change();
    if (before === null || !reference.isConnected) return;
    const shift = reference.getBoundingClientRect().top - before;
    if (Math.abs(shift) > 0.5) window.scrollBy({ top: shift, behavior: 'instant' }); // not the page's smooth scrolling
  }

  function reveal(items) {
    if (reduceMotion.matches || !Element.prototype.animate) return;
    items.forEach((item, index) => item.animate(
      [{ opacity: 0, transform: 'translateY(6px)' }, { opacity: 1, transform: 'none' }],
      { duration: 420, delay: Math.min(index, 7) * 35, easing: 'cubic-bezier(.2, .7, .2, 1)', fill: 'backwards' }
    ));
  }

  // Inline error + "Try again" on the link that failed (the next-page nav, or the "Load previous" holder).
  function showError(state, on, link = state.next) {
    if (!link) return;
    const error = link === state.next ? state.error : link.closest('.hb-collection__prev')?.querySelector('[data-hb-load-error]');
    if (error) error.hidden = !on;
    const label = on ? link.dataset.retryLabel || link.dataset.label : link.dataset.label;
    if (label && link.textContent !== label) link.textContent = label;
  }

  // "Showing 48 of 125", or "Showing 73–120 of 125" when the list starts mid-way (a deep link to ?page=N);
  // the bar marks the same stretch of the list.
  function renderStatus(state) {
    const { first, count, total, more } = state;
    const template = first > 1 ? more.dataset.hbShowingRange : more.dataset.hbShowing;
    if (state.status && template) {
      const text = template.replaceAll('[from]', first).replaceAll('[to]', count).replaceAll('[shown]', count).replaceAll('[total]', total);
      if (state.status.textContent.trim() !== text) state.status.textContent = text; // polite live region
    }
    if (state.bar && total) {
      state.bar.style.marginInlineStart = `${Math.min(100, ((first - 1) / total) * 100).toFixed(2)}%`;
      state.bar.style.width = `${Math.min(100, (Math.max(0, count - first + 1) / total) * 100).toFixed(2)}%`;
    }
  }

  // Page 1's editorial tile(s) as skeletons where the tile renders (after `position` cards), so filling in
  // page 1 from above ("Load previous") holds the tile's cells too. The section prints them on later pages
  // only, as <template data-hb-feature-skeleton data-position>.
  function withFeatureSkeletons(state, cards) {
    const tiles = [...state.section.querySelectorAll('template[data-hb-feature-skeleton]')]
      .map(template => ({ at: Number(template.dataset.position) || 4, node: template.content.firstElementChild?.cloneNode(true) }))
      .filter(tile => tile.node && tile.at <= cards.length);
    if (!tiles.length) return cards;
    const out = [];
    cards.forEach((card, index) => {
      out.push(card);
      tiles.filter(tile => tile.at === index + 1).forEach(tile => out.push(tile.node));
    });
    return out;
  }

  async function load(state, direction, { manual = false, keyboard = false } = {}) {
    const forward = direction === 'next';
    const link = forward ? state.next : state.previous;
    if (!link || state.busy) return;
    if (!manual) {
      if (state.failed || state.auto >= AUTO_PAGES) return;
      if (focusHolds(state)) { state.held = true; return; } // re-armed by releaseHeld()
      state.auto++;
    }
    const controller = new AbortController();
    state.busy = controller;
    const { grid } = state;
    const holder = forward ? null : link.closest('.hb-collection__prev');
    showError(state, false, link);
    link.classList.add('is-loading');
    link.setAttribute('aria-disabled', 'true');
    grid.setAttribute('aria-busy', 'true');
    const url = carry(link.href);
    const remaining = forward ? Math.min(state.pageSize, state.total - state.count) : state.pageSize;
    let bones = skeletons(remaining > 0 ? remaining : state.pageSize);
    // Filling in page 1 from above: its editorial tile comes along, so its cells are held as well.
    if (!forward && (Number(url.searchParams.get('page')) || 1) === 1 && !grid.querySelector(':scope > [data-hb-feature]')) bones = withFeatureSkeletons(state, bones);
    keep(holder, () => (forward ? grid.append(...bones) : grid.prepend(...bones)));
    try {
      const request = new URL(url);
      // Section Rendering API on Shopify: only this section's HTML. The preview serves the whole page.
      if (!isPreview && state.section.dataset.sectionId) request.searchParams.set('section_id', state.section.dataset.sectionId);
      const response = await fetch(request, { signal: controller.signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const incoming = doc.querySelector('[data-hb-collection]');
      const incomingGrid = incoming?.querySelector('[data-hb-collection-grid]');
      if (!incomingGrid) throw Object.assign(new Error('No product grid in the response'), { structural: true });

      // Product cards only, and never a product that is already shown. The editorial tile belongs to page 1:
      // it only comes along when page 1 itself is filled in above ("Load previous") and none is on the page yet.
      const seen = new Set([...grid.querySelectorAll('[data-hb-card][data-handle]')].map(card => card.dataset.handle));
      const takeFeature = !forward && !grid.querySelector(':scope > [data-hb-feature]');
      const items = [...incomingGrid.children].filter(item => {
        if (item.hasAttribute('data-hb-feature')) return takeFeature;
        if (!item.matches('.hb-collection__item')) return false;
        const handle = item.querySelector('[data-hb-card]')?.dataset.handle;
        if (!handle) return true;
        if (seen.has(handle)) return false;
        seen.add(handle);
        return true;
      }).map(item => document.adoptNode(item));

      // Focus on the link (or anywhere in its nav) when the cards arrive moves to the first new card, so keyboard
      // order never skips the pieces that just came in above it (see the focus step below).
      const focused = !!(forward ? state.more : holder)?.contains(document.activeElement);
      keep(holder, () => {
        if (bones.length) bones[0].before(...items);
        else if (forward) grid.append(...items);
        else grid.prepend(...items);
        bones.forEach(bone => bone.remove());
      });

      const incomingMore = incoming.querySelector('[data-hb-more]');
      state.total = Number(incomingMore?.dataset.hbTotal) || state.total;
      if (forward) {
        state.count = Number(incomingMore?.dataset.hbCount) || state.count + items.length;
        const nextHref = incomingMore?.querySelector('[data-hb-load-more]')?.getAttribute('href');
        if (nextHref) link.href = carry(nextHref).href;
        else { link.remove(); state.next = null; }
      } else {
        state.first = Number(incomingMore?.dataset.hbFirst) || Math.max(1, state.first - items.filter(item => item.matches('.hb-collection__item')).length);
        const previousHref = incoming.querySelector('[data-hb-load-previous]')?.getAttribute('href');
        if (previousHref) link.href = carry(previousHref).href;
        else { keep(items[0], () => holder?.remove()); state.previous = null; }
      }
      renderStatus(state);

      if (isPreview) simulate(items);
      const added = [...grid.children].filter(item => !item.hidden && items.includes(item));
      // Focus follows a tap or press of the link to the first new card (it lands where the link was); after a
      // key press that card is scrolled fully into view, so Enter never opens a piece the shopper cannot see.
      // An automatic load moves focus only if it was in the nav (it got there mid-load) and the card is in view;
      // otherwise focus stays put.
      const target = added.map(item => item.querySelector('.hb-card__title a')).find(Boolean);
      if (target && (manual || (focused && inView(state, target)))) {
        target.focus({ preventScroll: true });
        if (manual && keyboard && !inView(state, target)) bringIntoView(state, target.closest('li') || target);
      }
      state.failed = false;
      if (!state.grew) {
        state.grew = true;
        // The page is now longer than what a reload renders: restore by card (see restore()), not by pixels.
        if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
        window.addEventListener('scroll', queueSync, { passive: true });
      }
      reveal(added);
    } catch (error) {
      bones.forEach(bone => bone.remove());
      if (error.name === 'AbortError') {
        if (!manual) state.auto--;
      } else if (error.structural && manual) {
        location.assign(url); // the response is not a product page we can read: plain navigation
      } else {
        // Offline or a server error, either direction: stay (everything loaded so far stays) and offer "Try again".
        if (forward) state.failed = true; // and stop loading on its own until the shopper retries
        showError(state, true, link);
      }
    } finally {
      state.busy = null;
      grid.removeAttribute('aria-busy');
      link.classList.remove('is-loading');
      link.removeAttribute('aria-disabled');
      // Re-arm after either direction: a fresh observer reports the nav's current position, so a "Load more"
      // that came into range while another request ran (e.g. "Load previous") loads now.
      if (state.section.isConnected) watch(state);
    }
  }

  /* ---------- URL + place ----------
     The address bar follows the page whose cards are in view (?page=N, filters and sort kept), so a refresh,
     a shared link or a back navigation that is not served from the back/forward cache renders that page;
     the card that was in view is then scrolled back to the same spot. "Load previous" fills in above. */
  const probe = () => window.innerHeight * 0.3;
  function anchorItem(state) {
    const line = probe();
    let found = null;
    for (const item of state.grid.children) {
      if (!item.dataset.hbPage || item.hidden) continue;
      if (item.getBoundingClientRect().top > line) return found || item;
      found = item;
    }
    return found;
  }
  // Storage key for this listing at a page (default: the page in the address bar); page 1 has no param.
  function placeKey(page) {
    const url = new URL(location.href);
    url.searchParams.delete('section_id');
    const number = page || Number(url.searchParams.get('page')) || 1;
    if (number > 1) url.searchParams.set('page', String(number)); else url.searchParams.delete('page');
    url.searchParams.sort();
    return STORE + url.pathname + url.search;
  }
  const forget = key => { try { sessionStorage.removeItem(key); } catch { /* storage blocked */ } };

  // The tab title follows the address bar: the layout adds " – Page N" (general.page) to the page it rendered.
  const title = { base: null, page: '' };
  function readTitle(section) {
    if (title.base !== null || !section.dataset.hbPageTitle) return;
    const rendered = Number(new URL(location.href).searchParams.get('page')) || 1;
    title.page = section.dataset.hbPageTitle;
    const suffix = ` – ${title.page.replaceAll('[page]', rendered)}`;
    if (rendered <= 1) title.base = document.title;
    else if (document.title.endsWith(suffix)) title.base = document.title.slice(0, -suffix.length);
  }
  function syncTitle(page) {
    if (title.base === null) return;
    const next = page > 1 ? `${title.base} – ${title.page.replaceAll('[page]', page)}` : title.base;
    if (document.title !== next) document.title = next;
  }

  let syncTimer = 0;
  function queueSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(syncUrl, 150);
  }
  function syncUrl() {
    clearTimeout(syncTimer);
    const state = [...lists.values()].find(entry => entry.grew);
    const item = state && anchorItem(state);
    if (!item) return;
    const page = Number(item.dataset.hbPage) || 1;
    const url = new URL(location.href);
    if (page > 1) url.searchParams.set('page', String(page)); else url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    if (url.href !== location.href) {
      try { history.replaceState(history.state, '', url); } catch { return; /* rate-limited: next scroll retries */ }
    }
    syncTitle(page);
  }

  // Only a listing that grew (or was itself restored) saves its place; anything else leaves the browser's own
  // scroll restoration alone, and clears an older save for the same URL so it cannot override it.
  function savePlace() {
    const state = [...lists.values()].find(entry => entry.grew || entry.restored);
    const item = state && anchorItem(state);
    const handle = item?.querySelector('[data-hb-card]')?.dataset.handle;
    if (!handle) { forget(placeKey()); return; }
    try { sessionStorage.setItem(placeKey(Number(item.dataset.hbPage) || 1), JSON.stringify({ handle, top: Math.round(item.getBoundingClientRect().top) })); } catch { /* storage blocked */ }
  }

  function restore() {
    const type = performance.getEntriesByType?.('navigation')?.[0]?.type;
    const key = placeKey();
    let saved = null;
    if (type === 'back_forward' || type === 'reload') {
      try { saved = JSON.parse(sessionStorage.getItem(key) || 'null'); } catch { /* storage blocked */ }
    }
    forget(key);
    if (!saved?.handle) return;
    if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
    lists.forEach(state => { state.restored = true; });
    const card = [...document.querySelectorAll('[data-hb-collection-grid] [data-hb-card]')].find(node => node.dataset.handle === saved.handle);
    const item = card?.closest('[data-hb-collection-grid] > li');
    if (item) window.scrollTo({ top: window.scrollY + item.getBoundingClientRect().top - saved.top, behavior: 'instant' });
  }

  window.addEventListener('pagehide', () => {
    lists.forEach(state => state.busy?.abort());
    savePlace();
  });
  // Back/forward cache: the page comes back as it was; re-arm loading that pagehide stopped, and put the sheet
  // back to this page's sort and filters (a pick made just before leaving would otherwise still be ticked).
  window.addEventListener('pageshow', event => {
    if (!event.persisted) return;
    lists.forEach(state => { state.stopped = false; watch(state); });
    resetForms();
  });

  // The filter/sort sheet always shows the state of the page's own URL: the server marks it (`checked` /
  // `value` attributes; the preview stand-in sets the same defaults), so live state goes back to those defaults
  // after the browser restores a form or the back/forward cache restores the page.
  function resetForms() {
    document.querySelectorAll('[data-hb-filter-form] input').forEach(input => {
      if (input.type === 'radio' || input.type === 'checkbox') input.checked = input.defaultChecked;
      else if (input.type === 'number') input.value = input.defaultValue;
    });
  }

  // Theme editor: sections re-render in place.
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll?.('[data-hb-collection]').forEach(section => {
      const state = lists.get(section);
      if (state) { stop(state); lists.delete(section); }
    });
  });
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll?.('[data-hb-collection]').forEach(setup));

  /* ---------- preview only ----------
     The local preview serves static pages, so Shopify's server-side filtering and sorting are simulated here,
     including the active-filter chips and the empty state Shopify renders itself. A page that loads later
     (`batch`) is filtered and sorted within itself and never reorders cards already on screen: the static
     pages are not sorted across pages, which Shopify does on the server. On the live store none of this runs. */
  const params = new URLSearchParams(location.search);
  function simulate(batch = null) {
    const initial = !batch;
    const grid = document.querySelector('[data-hb-collection-grid]');
    if (!grid) return;
    const all = [...grid.querySelectorAll(':scope > .hb-collection__item:not(.hb-collection__item--skeleton)')];
    const items = initial ? all : batch.filter(item => item.matches('.hb-collection__item'));
    const feature = grid.querySelector(':scope > [data-hb-feature]');
    const availability = params.getAll('filter.v.availability');
    const minRaw = params.get('filter.v.price.gte');
    const maxRaw = params.get('filter.v.price.lte');
    const min = minRaw ? Number(minRaw) * 100 : 0;
    const max = maxRaw ? Number(maxRaw) * 100 : Infinity;
    const filtered = availability.length > 0 || Boolean(minRaw) || Boolean(maxRaw);
    let visible = 0;
    for (const item of items) {
      const price = Number(item.dataset.price);
      const available = item.dataset.available === 'true';
      const show = (!availability.length || availability.includes(available ? '1' : '0')) && (!Number.isFinite(price) || (price >= min && price <= max));
      item.hidden = !show;
      if (show) visible++;
    }
    const sort = params.get('sort_by');
    const compare = {
      'price-ascending': (a, b) => a.dataset.price - b.dataset.price,
      'price-descending': (a, b) => b.dataset.price - a.dataset.price,
      'title-ascending': (a, b) => a.dataset.title.localeCompare(b.dataset.title),
      'title-descending': (a, b) => b.dataset.title.localeCompare(a.dataset.title)
    }[sort];
    if (compare && items.length) {
      // The batch is contiguous in the grid: re-insert it, sorted, where it landed.
      const marker = document.createComment('');
      items[0].before(marker);
      marker.after(...[...items].sort(compare));
      marker.remove();
    }
    if (feature) {
      // Keep the editorial tile after the same number of visible cards it sits after on Shopify.
      const after = Number(feature.dataset.position || 4);
      // the cells before it: product cards and the builder cards (a builder card counts as one, as in Liquid)
      const shown = [...grid.querySelectorAll(':scope > .hb-collection__item:not(.hb-collection__item--skeleton):not([hidden]), :scope > .hb-collection__offer')];
      feature.hidden = shown.length < after;
      if (shown[after - 1] && shown[after - 1].nextElementSibling !== feature) shown[after - 1].after(feature);
    }
    if (!initial) return;
    if (sort) syncSortControls(sort);
    if (!filtered) return;
    const empty = document.querySelector('[data-hb-empty]');
    if (empty) empty.hidden = visible > 0;
    grid.hidden = visible === 0;
    document.querySelector('.hb-collection__more')?.toggleAttribute('hidden', visible === 0);
    const count = document.querySelector('[data-hb-results-count]');
    if (count) count.textContent = `${visible} ${visible === 1 ? 'piece' : 'pieces'}`;
    document.querySelectorAll('[data-hb-filter-form] input').forEach(input => {
      if (input.type === 'checkbox') input.checked = input.defaultChecked = params.getAll(input.name).includes(input.value);
      if (input.type === 'number') input.value = input.defaultValue = params.get(input.name) || '';
    });
    buildChips(availability, minRaw, maxRaw, min, max);
  }
  function syncSortControls(sort) {
    const radios = [...document.querySelectorAll('[data-hb-sort-radio]')];
    const fallback = radios.find(input => input.defaultChecked)?.value; // the listing's default, as rendered
    radios.forEach(input => { input.checked = input.defaultChecked = input.value === sort; });
    if (sort === fallback) return;
    // As Shopify prints a non-default sort: named beside "Filter + Sort" (phones: in the chip row), and kept by "Clear all".
    const name = radios.find(input => input.value === sort)?.nextElementSibling?.textContent.trim();
    if (name) {
      document.querySelectorAll('[data-hb-sort-current]').forEach(note => { note.lastElementChild.textContent = name; note.hidden = false; });
      const active = document.querySelector('[data-hb-active]');
      if (active) active.hidden = false;
    }
    document.querySelectorAll('[data-hb-filters-clear]').forEach(link => {
      const url = new URL(link.href);
      url.searchParams.set('sort_by', sort);
      link.href = url.pathname + url.search;
    });
  }
  function buildChips(availability, minRaw, maxRaw, min, max) {
    const badge = document.querySelector('[data-hb-filter-count]');
    const chips = [];
    const without = (name, value) => {
      const next = new URLSearchParams(params);
      const kept = next.getAll(name).filter(entry => value !== undefined && entry !== value);
      next.delete(name);
      next.delete('page');
      kept.forEach(entry => next.append(name, entry));
      const query = next.toString();
      return location.pathname + (query ? `?${query}` : '');
    };
    const labelFor = (name, value) => document.querySelector(`[data-hb-filter-form] input[name="${CSS.escape(name)}"][value="${CSS.escape(value)}"]`)?.closest('label')?.querySelector('.hb-chip')?.firstChild?.textContent.trim() || value;
    availability.forEach(value => chips.push({ label: labelFor('filter.v.availability', value), href: without('filter.v.availability', value) }));
    if (minRaw || maxRaw) {
      const next = new URLSearchParams(params);
      next.delete('filter.v.price.gte');
      next.delete('filter.v.price.lte');
      next.delete('page');
      const query = next.toString();
      const money = window.HumblerUI?.money || (cents => `$${cents / 100}`);
      const label = !maxRaw ? `From ${money(min)}` : !minRaw ? `Up to ${money(max)}` : `${money(min)}–${money(max)}`;
      chips.push({ label, href: location.pathname + (query ? `?${query}` : '') });
    }
    if (badge) { badge.textContent = String(chips.length); badge.hidden = chips.length === 0; }
    const active = document.querySelector('[data-hb-active]');
    const list = active?.querySelector('[data-hb-active-list]');
    if (!active || !list || !chips.length) return;
    const close = '<svg class="hb-icon hb-icon--xs" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
    const clearItem = list.querySelector('li:last-child');
    for (const chip of chips) {
      const li = document.createElement('li');
      const anchor = document.createElement('a');
      anchor.className = 'hb-collection__chip';
      anchor.href = chip.href;
      anchor.setAttribute('aria-label', `Remove filter: ${chip.label}`);
      anchor.textContent = chip.label;
      anchor.insertAdjacentHTML('beforeend', close);
      li.append(anchor);
      list.insertBefore(li, clearItem);
    }
    active.hidden = false;
  }

  const start = () => {
    revealCurrentPill();
    if (isPreview) simulate();
    resetForms();
    document.querySelectorAll('[data-hb-collection]').forEach(setup);
    restore();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start); else start();
})();
